<template>
  <div
    :class="height ? 'chart-fixed-height' : ''"
    :style="height ? { height: `${height}px`, position: 'relative' } : null"
  >
    <canvas ref="chart" />
  </div>
</template>
<script>
import Chart from 'chart.js';

export default {
  props: {
    type: { type: String, required: true },
    data: { type: Object, required: true },
    stacked: { type: Boolean, default: false },
    height: { type: Number, default: null },
    extraOptions: { type: Object, default: () => ({}) },
    plugins: { type: Array, default: () => [] },
  },
  mounted() {
    this.render(this.$props.type, this.$props.data);
  },
  methods: {
    getLocale(d) {
      const data = d;
      data.datasets.forEach((dataset, index) => {
        switch (dataset.label) {
          case 'InsertedEmptyParts':
            data.datasets[index].label = this.$t('part_status.inserted_empty_parts');
            break;
          case 'DeletedEmptyParts':
            data.datasets[index].label = this.$t('part_status.deleted_empty_parts');
            break;
          case 'InsertedTemporaryParts':
            data.datasets[index].label = this.$t('part_status.inserted_temporary_parts');
            break;
          case 'DeletedTemporaryParts':
            data.datasets[index].label = this.$t('part_status.deleted_temporary_parts');
            break;
          case 'TemporaryPartsWaitingApproval':
            data.datasets[index].label = this.$t('part_status.temporary_parts_waiting_approval');
            break;
          case 'PartsSentForApproval':
            data.datasets[index].label = this.$t('part_status.parts_sent_for_approval');
            break;
          case 'PartsSentForReview':
            data.datasets[index].label = this.$t('part_status.parts_sent_for_review');
            break;
          case 'RefusedParts':
            data.datasets[index].label = this.$t('part_status.refused_parts');
            break;
          case 'ApprovedParts':
            data.datasets[index].label = this.$t('part_status.approved_parts');
            break;
          case 'InsertedInventory':
            data.datasets[index].label = this.$t('part_status.inserted_inventory');
            break;
          case 'DeletedInventory':
            data.datasets[index].label = this.$t('part_status.deleted_inventory');
            break;
          case 'ReturnedFromSales':
            data.datasets[index].label = this.$t('part_status.returned_sales');
            break;
          case 'ReturnedFromShop':
            data.datasets[index].label = this.$t('part_status.returned_shop');
            break;
          case 'ReEntered':
            data.datasets[index].label = this.$t('part_status.re_entered');
            break;
          case 'Found':
            data.datasets[index].label = this.$t('part_status.found');
            break;
          case 'Sold':
            data.datasets[index].label = this.$t('part_status.sold');
            break;
          case 'Lost':
            data.datasets[index].label = this.$t('part_status.lost');
            break;
          case 'Damaged':
            data.datasets[index].label = this.$t('part_status.damaged');
            break;
          case 'DamagedFromSales':
            data.datasets[index].label = this.$t('part_status.damaged_sales');
            break;
          case 'DamagedFromShop':
            data.datasets[index].label = this.$t('part_status.damaged_shop');
            break;
          case 'DamagedFromLostParts':
            data.datasets[index].label = this.$t('part_status.damaged_lost');
            break;
          case 'EnteredParts':
          case 'EnteredPartsInventory':
          case 'EnteredInventory':
            data.datasets[index].label = this.$t('history.entered_parts_inventory');
            break;
          case 'InventoryIntake':
            data.datasets[index].label = this.$t('home.inventory_intake.chart_label');
            break;
          default:
            break;
        }
      });
      data.labels.forEach((label, index) => {
        switch (label) {
          case 'Total Activity':
            data.labels[index] = this.$t('home.charts.total_activity');
            break;
          case 'Past 24h Activity':
            data.labels[index] = this.$t('home.charts.past_24h_activity');
            break;
          default:
            break;
        }
      });
      return data;
    },
    render(type, data) {
      const datasetsCopy = data.datasets.map((ds) => {
        if (ds.label === 'SoldEbayLeko') {
          return {
            ...ds,
            data: ds.data.map(d => ({ x: d.x, y: d.y, amount: d.amount })),
          };
        }
        return ds;
      });

      // Localize labels
      const localizedData = this.getLocale({
        ...data,
        datasets: datasetsCopy,
        labels: data.labels.slice(),
      });

      const ctx = this.$refs.chart;
      const isBar = type === 'bar';
      const defaultScales = {
        ...(isBar
          ? {
              xAxes: [
                {
                  stacked: this.stacked,
                  barPercentage: 0.9,
                  categoryPercentage: 0.9,
                  maxBarThickness: 48,
                },
              ],
            }
          : {}),
        yAxes: [{ stacked: this.stacked, ticks: { beginAtZero: true } }],
      };
      const tooltipCallbacks = {
        label(tooltipItem, chartData) {
          const dataset = chartData.datasets[tooltipItem.datasetIndex];
          const v = dataset.data[tooltipItem.index];
          const originalLabel = data.datasets[tooltipItem.datasetIndex].label;
          if (originalLabel === 'SoldEbayLeko') {
            const y = v && typeof v === 'object' ? v.y : v;
            const amount = v && typeof v === 'object' ? v.amount : undefined;
            if (amount === undefined) return `${dataset.label}: ${y}`;
            return `${dataset.label}: ${y}, TotalAmount: ${amount} EUR`;
          }
          const value = v && typeof v === 'object' ? v.y : v;
          return `${dataset.label}: ${value}`;
        },
        title() { return ''; },
      };
      const options = {
        maintainAspectRatio: !this.height,
        ...this.extraOptions,
        legend: {
          labels: { boxWidth: 12 },
          ...(this.extraOptions.legend || {}),
        },
        tooltips: {
          callbacks: tooltipCallbacks,
          ...(this.extraOptions.tooltips || {}),
        },
        scales: this.extraOptions.scales || defaultScales,
      };
      new Chart(ctx, {
        type,
        data: localizedData,
        options,
        plugins: this.plugins,
      });
    },


  },
};
</script>
